import React from 'react';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';
import { Grid, LinearProgress, Typography, type Breakpoints } from '@mui/material';
import { useDeepCompareEffect } from '@fuse/hooks';

/* eslint-disable-next-line import/no-cycle */
import { ErrorAlert, useSettings, Utils } from 'app/main/components';
import { type PaginationType } from 'app/main/components/hooks/usePagination';
import { type FiltersProps } from 'app/main/components/hooks/useFilters';
import { type SelectionProps } from 'app/main/components/hooks/useSelection';
import { type ItemLayoutProps } from 'app/main/components/GridList';

/* eslint-disable-next-line import/no-cycle */
import useViewState from '../components/useViewState';
import ViewPagination from '../pagination/ViewPagination';
import ViewFooter, { type ViewFooterCustomInfoProps } from '../components/ViewFooter';

export interface ViewGridProps {
	classes?: { list?: string };
	columns?: Partial<Breakpoints['values']>;
	id?: string;
	module: string;
	storedProcedure?: string;
	// TODO: definire meglio il tipo (vedi ViewList)
	itemLayout: React.FC<ItemLayoutProps>;
	defaultFilters?: FiltersProps['defaultFilters'];
	defaultQuery?: any;
	filters?: FiltersProps['filters'];
	paginationProps?: PaginationType;
	refresh?: boolean;
	updateRefresh?: () => void;
	isSelecting?: boolean;
	selectedItems?: SelectionProps['selectedItems'];
	slots?: {
		footerCustomInfo?: (props: ViewFooterCustomInfoProps) => React.ReactNode;
	};
	showBottomPagination?: boolean;
	showFilters?: boolean;
	showExportButton?: boolean;
}

export default function ViewGrid({
	itemLayout: ItemLayout,
	columns: defaultColumns,
	showBottomPagination = true,
	showFilters = true,
	showExportButton = true,
	...props
}: ViewGridProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings(props.module);

	const { ref, width = 0 } = useResizeDetector({ handleHeight: false });

	const columns = { lg: 3, md: 3, sm: 2, xs: 1, ...(defaultColumns || {}) };

	const [items, setItems] = React.useState([]);
	const [additionalInfo, setAdditionalInfo] = React.useState<any>({});
	const [error, setError] = React.useState<any>();

	const [refresh, setRefresh] = React.useState(true);
	const updateRefresh = () => setRefresh(value => !value);

	const viewState = useViewState({
		id: props.id,
		module: props.module,
		filter: {
			defaultFilters: [
				...(props.defaultFilters || []),
				{ filt_field: `${settings.modu_short}_deleted`, filt_clause: 'not_equal', filt_value: 1 }
			],
			defaultQuery: props.defaultQuery,
			filters: props.filters
		}
	});

	const paginationProps = props.paginationProps || viewState.paginationProps;

	const { filterProps, orderProps, paginatorProps } = paginationProps;
	const { nPage, nRows, setMaxRows, isLoading, setIsLoading } = paginatorProps;

	useDeepCompareEffect(() => {
		const controller = new AbortController();

		setIsLoading(true);
		Utils.executeStored({
			action: props.storedProcedure || `${settings.modu_external ? 'ext_' : ''}st_${props.module}_list`,
			params: {
				n_page: nPage,
				n_rows: nRows,
				filters: filterProps?.filters,
				query: filterProps?.query,
				order_by: orderProps?.orderString
			},
			dispatch,
			signal: controller.signal,
			successHandler: response => {
				const { n_record, list, ...other } = response.data;
				setMaxRows(n_record);
				setItems(list || []);
				setAdditionalInfo(other);
				setError(null);
				setIsLoading(false);
			},
			errorHandler: ex => {
				setError(ex);
				setIsLoading(false);
			}
		});

		return () => {
			controller.abort();
		};
	}, [
		dispatch,
		filterProps?.filters,
		filterProps?.query,
		nPage,
		nRows,
		orderProps?.orderBy,
		orderProps?.orderOperator,
		props.module,
		props.refresh,
		refresh,
		setMaxRows
	]);

	const footerCustomInfo = props.slots?.footerCustomInfo
		? props.slots?.footerCustomInfo({ ...additionalInfo, items })
		: undefined;

	return (
		<>
			<ViewPagination
				className="sticky top-0 z-99"
				module={props.module}
				paginationProps={paginationProps}
				updateRefresh={props.updateRefresh || updateRefresh}
				// isSelecting={isSelecting}
				// selectedItems={selectedItems}
				showFilters={showFilters}
				showExportButton={showExportButton}
			/>
			<div className={clsx('h-full overflow-y-auto py-8', props.classes?.list)}>
				{error ? (
					<ErrorAlert error={error} />
				) : !isLoading && settings ? (
					items.length === 0 ? (
						<Typography className="p-16 italic" align="center" color="textSecondary">
							{t('LIST_NO_ITEMS')}
						</Typography>
					) : (
						<Grid ref={ref} className="px-16" container spacing={1} columns={columns}>
							{items.map((item, i) => (
								<Grid key={i} size={1}>
									<ItemLayout
										width={width}
										settings={settings}
										model={item}
										buttonProps={{
											settings,
											model: item,
											filters: filterProps?.filters,
											updateRefresh: props.updateRefresh || updateRefresh
										}}
										isSelecting={props.isSelecting}
									/>
								</Grid>
							))}
						</Grid>
					)
				) : (
					<LinearProgress variant="query" />
				)}
			</div>
			{showBottomPagination ? (
				<ViewFooter paginatorProps={paginatorProps} customInfo={footerCustomInfo} />
			) : (
				<></>
			)}
		</>
	);
}
